import { Component, inject } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
    FieldFieldComponent,
    FieldMappingComponent,
    FieldRowComponent,
    InstructionsComponent,
    TitledContainerComponent,
} from '@r1-compass-library/components';
import { PicklistElement } from '@r1-compass-library/models';
import { Uuid } from '@r1-compass-library/utilities/uuid';
import { map } from 'rxjs';
import { Dependency } from '@r1-compass-library/models/dependencies.model';
import {
    DisplayAssetDependencies,
    DisplayAssetDependenciesListComponent,
} from '@r1-compass-library/components/display-asset-dependencies-list/display-asset-dependencies-list.component';
import { DivisionBasicSettingsFormService } from '../division-basic-settings-form.service';

export type AdditionalFieldsPicklistDialogData = {
    index: number;
    data: PicklistElement[];
    formService: DivisionBasicSettingsFormService;
    additionalFieldId: string;
};

@Component({
    selector: 'r1-compass-app-additional-fields-mapper-picklist-dialog',
    imports: [
        FieldFieldComponent,
        FieldMappingComponent,
        FieldRowComponent,
        InstructionsComponent,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        MatDialogModule,
        TitledContainerComponent,
        InstructionsComponent,
    ],
    template: `
        <r1-compass-lib-titled-container
            title="Add Picklist Elements"
            [class]="{ 'scrollable-div': picklistAtIndex?.length! > 3 }">
            <div>
                <r1-compass-lib-instructions headerText="Instructions">
                    Add picklist elements to be used.
                </r1-compass-lib-instructions>
                @for (group of picklistAtIndex?.controls; track $index) {
                    <form [formGroup]="group">
                        <r1-compass-lib-field-mapping
                            [index]="$index"
                            [totalRequiredRows]="0"
                            [rowOptionButtons]="[{ value: 'delete', display: 'Delete', show: true }]"
                            (clickOption)="deletePicklistRow($index, group.value.id!)">
                            <r1-compass-lib-field-row [fieldRowWidth]="2">
                                <r1-compass-lib-field-field [fieldWidth]="1">
                                    <mat-form-field>
                                        <mat-label>Name</mat-label>
                                        <input matInput formControlName="name" />
                                    </mat-form-field>
                                </r1-compass-lib-field-field>
                                <r1-compass-lib-field-field [fieldWidth]="1">
                                    <mat-form-field>
                                        <mat-label>Value</mat-label>
                                        <input matInput formControlName="value" />
                                    </mat-form-field>
                                </r1-compass-lib-field-field>
                            </r1-compass-lib-field-row>
                        </r1-compass-lib-field-mapping>
                    </form>
                }
                <button mat-raised-button color="primary" (click)="addPicklistRow()" type="button">+Add</button>
                <div id="footer-container">
                    <div class="cancel-button">
                        <button mat-raised-button (click)="onCancel()" type="button">Cancel</button>
                    </div>
                    <div>
                        <button
                            mat-raised-button
                            color="primary"
                            [disabled]="!picklistAtIndex?.valid"
                            (click)="onSave()"
                            type="submit">
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </r1-compass-lib-titled-container>
    `,
    styles: `
        #footer-container {
            display: flex;
            justify-content: flex-end;
        }

        .scrollable-div {
            height: 600px;
            overflow-y: auto;
        }

        .cancel-button {
            padding-right: 10px;
        }
    `,
})
export class PicklistDialogComponent {
    readonly #dialogRef = inject<MatDialogRef<PicklistDialogComponent>>(MatDialogRef);
    readonly #data = inject<AdditionalFieldsPicklistDialogData>(MAT_DIALOG_DATA);
    readonly #fb = inject(NonNullableFormBuilder);
    readonly #dialog = inject(MatDialog);

    protected formService = this.#data.formService;

    private picklistElementsFormArray = this.formService.divisionBasicSettingsForm.controls.additionalFields.controls;
    protected picklistAtIndex = this.picklistElementsFormArray.at(this.#data.index)?.controls.picklistElements;

    // Used to store initial picklist values in case of cancellation
    private picklistAtIndexClone = this.picklistAtIndex?.getRawValue();

    protected onSave() {
        this.#dialogRef.close(this.#data.index);
        this.formService.divisionBasicSettingsForm.markAllAsDirty();
    }

    protected onCancel() {
        // If Cancelled, empty Picklist and fill it back up with its original elements from before changes were made.
        this.picklistAtIndex?.clear();
        if (this.picklistAtIndexClone) {
            for (const picklistElement of this.picklistAtIndexClone) {
                this.picklistAtIndex?.push(
                    this.#fb.group({
                        id: this.#fb.control(picklistElement.id),
                        name: this.#fb.control(picklistElement.name),
                        value: this.#fb.control(picklistElement.value),
                    })
                );
            }
        }
        this.#dialogRef.close(this.#data.index);
    }

    protected addPicklistRow() {
        this.picklistAtIndex?.push(
            this.#fb.group({
                id: this.#fb.control(Uuid.generate()),
                name: this.#fb.control('', Validators.required),
                value: this.#fb.control('', Validators.required),
            })
        );
    }

    protected deletePicklistRow(index: number, rowId: string) {
        this.formService
            .getPlaysDependentOnAdditionalField(this.#data.additionalFieldId)
            .pipe(
                map(dependencies => {
                    return dependencies.filter(dep => {
                        const match = dep.config?.definition?.additionalPlayFields?.find(field => {
                            const matches = (field.picklistElements ?? []).find(dependentElement => {
                                const picklistFieldMatches =
                                    dependentElement.id === rowId &&
                                    dependentElement.value === this.picklistAtIndex?.at(index).value.value;
                                return picklistFieldMatches;
                            });
                            return matches;
                        });
                        return match !== undefined && match.value === this.picklistAtIndex?.at(index).value.id;
                    });
                })
            )
            .subscribe(dependentPlays => {
                if (dependentPlays.length > 0) {
                    const playData: Dependency[] = dependentPlays.map(play => ({
                        assetId: undefined,
                        playId: play.id,
                        name: play.name,
                    }));
                    this.#dialog
                        .open<DisplayAssetDependenciesListComponent, DisplayAssetDependencies>(
                            DisplayAssetDependenciesListComponent,
                            {
                                width: '600px',
                                disableClose: true,
                                data: {
                                    dependencies: playData,
                                    dialogText: `This additional field picklist option is being used by ${playData.length} plays. It must be removed from each before deleting.`,
                                },
                                autoFocus: false,
                            }
                        )
                        .afterClosed()
                        .subscribe();
                } else {
                    this.picklistAtIndex?.removeAt(index);
                    this.picklistAtIndex?.markAllAsDirty();
                    this.formService.divisionBasicSettingsForm.markAllAsDirty();
                }
            });
    }
}
